import {
  Alert,
  Button as MantineButton,
  CopyButton,
  Divider,
  Loader,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';
import {
  IconAlertCircle,
  IconBrandWhatsapp,
  IconCheck,
  IconCopy,
  IconMail,
  IconRefresh,
  IconUserPlus,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { inviteCodesApi } from 'src/Services/API';

import styles from './InviteTypeModal.module.scss';
import { InviteTypeModalInnerProps } from './InviteTypeModal.types';

const buildInvitationMessage = (code: string, assetName: string) => {
  const joinUrl = `${window.location.origin}/join?code=${code}`;

  return [
    `Salut ! Je viens de t'ajouter sur l'espace privé de ${assetName} (Share Heritage).`,
    '',
    `C'est là qu'on va centraliser le planning, les infos pratiques et les photos souvenirs.`,
    '',
    `Clique ici pour rejoindre : ${joinUrl}`,
    '',
    `Ton code d'accès : ${code}`,
    '',
    `(Si tu as déjà un compte, va dans ton profil (en haut à droite) > Rejoindre un bien)`,
  ].join('\n');
};

const InviteTypeModal = ({
  context,
  id,
  innerProps,
}: ContextModalProps<InviteTypeModalInnerProps>) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { assetId, onOpenAddMemberModal, onOpenInviteByEmailModal } = innerProps;

  const { data: inviteCode, isLoading: isFetchLoading } = useQuery({
    queryKey: ['fetch-invite-code', assetId],
    queryFn: async () => {
      const response = await inviteCodesApi.inviteCodesControllerFindByAsset(assetId);
      return response.data;
    },
    enabled: !!assetId,
  });

  const [createError, setCreateError] = useState(false);
  const hasTriggeredCreate = useRef(false);

  const { mutate: createCode, isLoading: isCreateLoading } = useMutation({
    mutationFn: async () => {
      const response = await inviteCodesApi.inviteCodesControllerCreate(assetId, {});
      return response.data;
    },
    onSuccess: () => {
      setCreateError(false);
      queryClient.invalidateQueries(['fetch-invite-code', assetId]);
    },
    onError: () => {
      setCreateError(true);
    },
  });

  useEffect(() => {
    if (!isFetchLoading && inviteCode === null && !hasTriggeredCreate.current) {
      hasTriggeredCreate.current = true;
      createCode();
    }
    // createCode is a stable reference from useMutation, hasTriggeredCreate is a ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchLoading, inviteCode]);

  const code = inviteCode?.code ?? '';
  const assetName = inviteCode?.asset?.name ?? '';

  const invitationMessage = useMemo(
    () => buildInvitationMessage(code, assetName),
    [code, assetName]
  );

  const handleWhatsApp = useCallback(() => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(invitationMessage)}`;
    window.open(whatsappUrl, '_blank');
  }, [invitationMessage]);

  const handleAddMember = useCallback(() => {
    context.closeModal(id);
    onOpenAddMemberModal();
  }, [context, id, onOpenAddMemberModal]);

  const handleInviteByEmail = useCallback(() => {
    context.closeModal(id);
    onOpenInviteByEmailModal();
  }, [context, id, onOpenInviteByEmailModal]);

  const isLoading = isFetchLoading || (isCreateLoading && !inviteCode);

  return (
    <div className={styles.container}>
      <div className={styles.codeSection}>
        <Text className={styles.sectionTitle}>
          {t('admin.inviteModal.codeTitle', "Code d'invitation")}
        </Text>
        <Text className={styles.sectionDescription}>
          {t(
            'admin.inviteModal.codeDescription',
            "Partagez ce code avec vos proches pour qu'ils rejoignent votre bien"
          )}
        </Text>

        {isLoading ? (
          <div className={styles.loaderContainer}>
            <Loader size="sm" />
          </div>
        ) : createError && !inviteCode ? (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {t('admin.inviteModal.createError', "Impossible de générer le code d'invitation.")}
            <MantineButton
              variant="light"
              color="red"
              size="compact-sm"
              mt="xs"
              onClick={() => createCode()}
            >
              {t('admin.inviteModal.retry', 'Réessayer')}
            </MantineButton>
          </Alert>
        ) : (
          <>
            <div className={styles.codeRow}>
              <Text className={styles.code}>{code}</Text>
            </div>
            <div className={styles.shareButtons}>
              <MantineButton
                className={styles.whatsappButton}
                leftSection={<IconBrandWhatsapp size={18} />}
                color="green"
                onClick={handleWhatsApp}
                fullWidth
              >
                {t('admin.inviteModal.sendWhatsApp', 'Envoyer par WhatsApp')}
              </MantineButton>
              <CopyButton value={invitationMessage} timeout={2000}>
                {({ copied, copy }) => (
                  <MantineButton
                    className={styles.copyButton}
                    leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    variant={copied ? 'light' : 'outline'}
                    color={copied ? 'teal' : undefined}
                    onClick={copy}
                    fullWidth
                  >
                    {copied
                      ? t('admin.inviteModal.copied', 'Copié !')
                      : t('admin.inviteModal.copyInvitation', "Copier l'invitation")}
                  </MantineButton>
                )}
              </CopyButton>
            </div>
            <UnstyledButton className={styles.regenerateButton} onClick={() => createCode()}>
              <IconRefresh size={16} />
              <Text size="sm">{t('admin.inviteModal.regenerate', 'Générer un nouveau code')}</Text>
            </UnstyledButton>
          </>
        )}
      </div>

      <Divider
        label={t('admin.inviteModal.or', 'ou')}
        labelPosition="center"
        className={styles.divider}
      />

      <UnstyledButton className={styles.optionButton} onClick={handleInviteByEmail}>
        <div className={styles.iconContainer}>
          <IconMail size={32} />
        </div>
        <div className={styles.textContainer}>
          <Text className={styles.optionTitle}>
            {t('admin.inviteModal.inviteByEmailTitle', 'Inviter par email')}
          </Text>
          <Text className={styles.optionDescription}>
            {t(
              'admin.inviteModal.inviteByEmailDescription',
              "Choisissez son rôle et ses permissions avant l'envoi — la personne arrivera avec exactement ces droits"
            )}
          </Text>
        </div>
      </UnstyledButton>

      <UnstyledButton className={styles.optionButton} onClick={handleAddMember}>
        <div className={styles.iconContainer}>
          <IconUserPlus size={32} />
        </div>
        <div className={styles.textContainer}>
          <Text className={styles.optionTitle}>
            {t('admin.inviteModal.addMemberTitle', 'Ajouter un membre')}
          </Text>
          <Text className={styles.optionDescription}>
            {t(
              'admin.inviteModal.addMemberDescription',
              'Renseignez son nom et son email — il pourra rejoindre à tout moment, vous pouvez déjà tout configurer'
            )}
          </Text>
        </div>
      </UnstyledButton>
    </div>
  );
};

export default InviteTypeModal;
